import { isEmpty } from '@ember/utils';
import type { Body, Meta, UppyFile } from '@uppy/core';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import StreamSource from '@linc-technologies/linced-common/enums/stream-source';
import type Media from '@linc-technologies/linced-common/models/media';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import type Store from '@linc-technologies/linced-common/services/store';

// AttachmentFile is an Uppy file, plus the Cloudflare uid the uppy service sets
// on the file state for image and video uploads.
type AttachmentFile = UppyFile<Meta, Body> & { cfUid?: string };

// CloudflareBody is the body of a Cloudflare upload, which carries the media id
// instead of an uploadURL.
type CloudflareBody = { result: { id: string } };

// UploadResponse is the response Uppy passes to `upload-success`.
type UploadResponse = NonNullable<UppyFile<Meta, CloudflareBody>['response']>;

// mediaKey returns the key, or throws if the upload has no media key.
function mediaKey(key: string | undefined): string {
  if (!key) throw new Error('uppy: the upload has no media key');
  return key;
}

// onAttachmentUpload builds the media record for a completed upload and
// returns the attachment entry. Callers append it themselves (e.g.
// `this.attachments = [...this.attachments, onAttachmentUpload(...)]`) since
// `attachments` is a plain array, not an EmberArray we can push onto here.
export function onAttachmentUpload(
  store: Store,
  sharedWith: number,
  schoolId: School,
  personId: Person,
  file: AttachmentFile,
  upload: UploadResponse,
  mediaType: number,
) {
  let mediaSource: number = MediaSource.UNSPECIFIED;
  let storageKey = '';
  switch (mediaType) {
    case MediaType.IMAGE:
    case MediaType.VIDEO:
      storageKey = mediaKey(file.cfUid);
      mediaSource = MediaSource.CLOUDFLARE;
      break;
    default:
      // split always returns at least one item.
      mediaType =
        MediaType.getValue(file.type.split('/')[0] as string) || MediaType.FILE;
      storageKey = mediaKey(upload.uploadURL?.match(/files\/(.+)\+/)?.[1]);
      mediaSource = MediaSource.INTERNAL;
      break;
  }

  // Not createRecord<Media>: it checks the props, and these are plain numbers.
  const record = store.createRecord('media', {
    schoolId,
    personId,
    sharedWith,
    mediaType,
    mediaSource,
    mimeType: file.type,
    fileName: file.name,
    storageKey,
    fileSize: file.size,
  }) as Media;

  if (mediaType === MediaType.VIDEO) {
    const video = store.createFragment('media/video', {
      streamSource: StreamSource.CLOUDFLARE_STREAM,
      streamId: storageKey,
      streamProcessed: false,
      poster: `https://customer-xjss1s0jhbnjqnbe.cloudflarestream.com/${storageKey}/thumbnails/thumbnail.jpg`,
    });
    record.set('video', video);
  }
  return { file, upload, record };
}

// copyAttachment makes another unsaved media record for the same uploaded
// file. Media belongs to a single post, so each post of a bulk create needs
// its own record; sharing one moved it from post to post.
export function copyAttachment(store: Store, record: Media) {
  return store.createRecord<Media>('media', {
    schoolId: record.belongsTo('schoolId').value(),
    personId: record.belongsTo('personId').value(),
    sharedWith: record.sharedWith,
    mediaType: record.mediaType,
    mediaSource: record.mediaSource,
    mimeType: record.mimeType,
    fileName: record.fileName,
    storageKey: record.storageKey,
    fileSize: record.fileSize,
    video: record.video?.copy() ?? null,
  });
}

export function getKeyAndSourceFromUpload(upload: UploadResponse) {
  let key = '';
  let mediaSource: number = 0;
  if (!isEmpty(upload.uploadURL)) {
    // Internal upload
    key = mediaKey(upload.uploadURL?.match(/files\/(.+)\+/)?.[1]);
    mediaSource = MediaSource.INTERNAL;
  } else {
    // Cloudflare upload
    key = mediaKey(upload.body?.result?.id);
    mediaSource = MediaSource.CLOUDFLARE;
  }
  return { key, mediaSource };
}
